import { Controller } from "@hotwired/stimulus"

const SOUND_KEY = "ticketfy.checkin.sound"
const WAKE_LOCK_KEY = "ticketfy.checkin.wakeLock"
const AUTO_SCAN_KEY = "ticketfy.checkin.autoScan"
const SUCCESS_RESULTS = new Set(["valid"])

export default class extends Controller {
  static targets = ["sheet", "sound", "wakeLock", "wakeLockHint", "autoScan"]

  connect() {
    this._wakeLockSentinel = null
    this._wakeLockRequesting = false
    this._audio = null
    this._onOutcome = (event) => this.feedback(event)
    this._onVisibility = () => this.onVisibilityChange()
    this._onKeydown = (event) => this.onKeydown(event)
    this._unlock = () => {
      if (this.soundEnabled) this.ensureAudio()
    }

    document.addEventListener("checkin:outcome", this._onOutcome)
    document.addEventListener("visibilitychange", this._onVisibility)
    window.addEventListener("keydown", this._onKeydown)
    this.element.addEventListener("pointerdown", this._unlock, { once: true })

    this.syncToggles()
    this.applyWakeLock()
  }

  disconnect() {
    document.removeEventListener("checkin:outcome", this._onOutcome)
    document.removeEventListener("visibilitychange", this._onVisibility)
    window.removeEventListener("keydown", this._onKeydown)
    this.element.removeEventListener("pointerdown", this._unlock)
    this.releaseWakeLock()
    this._audio?.close?.()
    this._audio = null
  }

  openSettings() {
    if (!this.hasSheetTarget) return

    this.sheetTarget.hidden = false
    this.syncToggles()
  }

  closeSettings() {
    if (!this.hasSheetTarget) return

    this.sheetTarget.hidden = true
  }

  onKeydown(event) {
    if (event.key === "Escape" && this.hasSheetTarget && !this.sheetTarget.hidden) {
      this.closeSettings()
    }
  }

  async toggleSound() {
    const enabled = this.hasSoundTarget ? this.soundTarget.checked : true
    this.writeFlag(SOUND_KEY, enabled)
    if (enabled) {
      await this.ensureAudio()
      this.playTest()
    }
  }

  toggleWakeLock() {
    const enabled = this.hasWakeLockTarget ? this.wakeLockTarget.checked : true
    this.writeFlag(WAKE_LOCK_KEY, enabled)
    this.applyWakeLock()
  }

  toggleAutoScan() {
    const enabled = this.hasAutoScanTarget ? this.autoScanTarget.checked : true
    this.writeFlag(AUTO_SCAN_KEY, enabled)
  }

  onVisibilityChange() {
    if (document.visibilityState === "visible") this.applyWakeLock()
  }

  async feedback(event) {
    if (!this.soundEnabled) return

    const result = event.detail?.result
    if (!result || result === "idle") return

    await this.ensureAudio()
    if (result === "preview") {
      this.playTest()
      this.vibrate(40)
      return
    }

    const success = SUCCESS_RESULTS.has(result)
    if (success) {
      this.playSuccess()
      this.vibrate(80)
    } else {
      this.playError()
      this.vibrate([200, 80, 200])
    }
  }

  syncToggles() {
    if (this.hasSoundTarget) this.soundTarget.checked = this.soundEnabled
    if (this.hasAutoScanTarget) this.autoScanTarget.checked = this.autoScanEnabled
    this.syncWakeLockToggle()
  }

  syncWakeLockToggle() {
    if (!this.hasWakeLockTarget) return

    const supported = this.wakeLockSupported
    this.wakeLockTarget.disabled = !supported
    this.wakeLockTarget.checked = supported && this.wakeLockEnabled
    if (this.hasWakeLockHintTarget) this.wakeLockHintTarget.hidden = supported
  }

  get soundEnabled() {
    return this.readFlag(SOUND_KEY, true)
  }

  get wakeLockEnabled() {
    return this.readFlag(WAKE_LOCK_KEY, true)
  }

  get autoScanEnabled() {
    return this.readFlag(AUTO_SCAN_KEY, true)
  }

  get wakeLockSupported() {
    return "wakeLock" in navigator
  }

  readFlag(key, fallback) {
    try {
      const value = localStorage.getItem(key)
      if (value === null) return fallback
      return value === "1" || value === "true"
    } catch {
      return fallback
    }
  }

  writeFlag(key, enabled) {
    try {
      localStorage.setItem(key, enabled ? "1" : "0")
    } catch {
      // private mode can block storage
    }
  }

  async applyWakeLock() {
    if (!this.wakeLockEnabled || !this.wakeLockSupported) {
      this.releaseWakeLock()
      return
    }

    if (document.visibilityState !== "visible") return
    if (this._wakeLockSentinel || this._wakeLockRequesting) return

    this._wakeLockRequesting = true
    try {
      this._wakeLockSentinel = await navigator.wakeLock.request("screen")
      this._wakeLockSentinel.addEventListener("release", () => {
        this._wakeLockSentinel = null
      })
    } catch {
      this._wakeLockSentinel = null
    } finally {
      this._wakeLockRequesting = false
    }
  }

  releaseWakeLock() {
    try {
      this._wakeLockSentinel?.release?.()
    } catch {
      // already released
    }
    this._wakeLockSentinel = null
  }

  async ensureAudio() {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext
    if (!AudioContextClass) return null

    if (!this._audio) this._audio = new AudioContextClass()
    if (this._audio.state === "suspended") await this._audio.resume()
    return this._audio
  }

  playSuccess() {
    this.playTone(880, 0.09, 0)
    this.playTone(1175, 0.12, 0.1)
  }

  playError() {
    this.playTone(196, 0.22, 0)
  }

  playTest() {
    this.playTone(784, 0.08, 0)
  }

  playTone(frequency, duration, when = 0) {
    const ctx = this._audio
    if (!ctx) return

    const oscillator = ctx.createOscillator()
    const gain = ctx.createGain()
    oscillator.type = "sine"
    oscillator.frequency.value = frequency

    const start = ctx.currentTime + when
    gain.gain.setValueAtTime(0.0001, start)
    gain.gain.exponentialRampToValueAtTime(0.18, start + 0.012)
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration)

    oscillator.connect(gain)
    gain.connect(ctx.destination)
    oscillator.start(start)
    oscillator.stop(start + duration + 0.02)
  }

  vibrate(pattern) {
    try {
      navigator.vibrate?.(pattern)
    } catch {
      // iOS Safari não implementa vibrate
    }
  }
}
